import { formatDuration, initials } from "@/lib/format";
import { Phone, PhoneOff, Mic, MicOff, WifiOff } from "lucide-react";

// Renders the active call UI for calling / incoming / connected states.
export function CallOverlay({ call, label, accent = "#0e8f7e", fixed = false }) {
  if (call.callState === "idle") return null;
  const name = call.peerName || label || "Contact";
  const net = call.netState || "connecting";
  const level = Math.max(0, Math.min(1, call.level || 0));
  const connected = call.callState === "connected";

  // Status line reflects both the call phase and live connection health.
  let status;
  if (call.callState === "calling") status = "Calling…";
  else if (call.callState === "incoming") status = "Incoming voice call";
  else if (net === "connecting") status = "Connecting…";
  else if (net === "reconnecting") status = "Reconnecting…";
  else status = formatDuration(call.seconds);

  const ringActive = call.callState !== "connected" || net === "reconnecting";
  // Avatar glows brighter as the peer speaks (getStats audioLevel).
  const glow = connected && net !== "reconnecting"
    ? `0 0 ${12 + level * 60}px ${Math.round(6 + level * 24)}px ${accent}${level > 0.05 ? "55" : "22"}`
    : "none";

  const qualityColor = net === "reconnecting" ? "#ff5470" : net === "poor" ? "#f5a623" : "#22c55e";
  const bars = net === "reconnecting" ? 1 : net === "poor" ? 2 : 3;

  return (
    <div
      className={`${fixed ? "fixed z-[60]" : "absolute z-40"} inset-0 flex flex-col items-center justify-between px-6 py-10 text-white`}
      style={{ background: `radial-gradient(120% 80% at 50% 0%, ${accent}22 0%, #0a1512 55%, #050a08 100%)`, backdropFilter: "blur(6px)" }}
      data-testid="call-overlay"
    >
      <div className="flex flex-col items-center gap-4 pt-10">
        <div
          className={`flex h-28 w-28 items-center justify-center rounded-full text-4xl font-semibold transition-shadow duration-150 ${ringActive ? "sc-pulse-ring" : ""}`}
          style={{ background: accent, boxShadow: glow }}
        >
          {initials(name)}
        </div>
        <div className="text-center">
          <div className="text-xl font-semibold">{name}</div>
          <div className="mt-1 flex items-center justify-center gap-2 text-sm text-white/70" data-testid="call-status-text">
            {connected && (
              net === "reconnecting"
                ? <WifiOff className="h-4 w-4" style={{ color: qualityColor }} aria-hidden="true" />
                : (
                  <span className="flex items-end gap-[2px]" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="w-[3px] rounded-sm" style={{ height: 6 + i * 4, background: i < bars ? qualityColor : "rgba(255,255,255,0.25)" }} />
                    ))}
                  </span>
                )
            )}
            <span>{status}</span>
          </div>
          {connected && net === "poor" && (
            <div className="mt-1 text-xs text-[#f5a623]">Weak connection</div>
          )}
        </div>
      </div>

      {call.callState === "incoming" ? (
        <div className="flex items-center gap-16">
          <button onClick={call.rejectCall} className="flex flex-col items-center gap-2" data-testid="reject-call-button">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg transition-transform hover:scale-105 active:scale-95">
              <PhoneOff className="h-7 w-7" aria-hidden="true" />
            </span>
            <span className="text-xs text-white/70">Decline</span>
          </button>
          <button onClick={call.acceptCall} className="flex flex-col items-center gap-2" data-testid="accept-call-button">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#22c55e] shadow-lg transition-transform hover:scale-105 active:scale-95 sc-pulse-ring">
              <Phone className="h-7 w-7" aria-hidden="true" />
            </span>
            <span className="text-xs text-white/70">Accept</span>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-8">
          {call.callState === "connected" && (
            <button onClick={call.toggleMute} className="flex flex-col items-center gap-2" data-testid="toggle-mute-button">
              <span className={`flex h-14 w-14 items-center justify-center rounded-full transition-colors ${call.muted ? "bg-white text-[#0a1512]" : "bg-white/15 hover:bg-white/25"}`}>
                {call.muted ? <MicOff className="h-6 w-6" aria-hidden="true" /> : <Mic className="h-6 w-6" aria-hidden="true" />}
              </span>
              <span className="text-xs text-white/70">{call.muted ? "Unmute" : "Mute"}</span>
            </button>
          )}
          <button onClick={() => call.endCall(true)} className="flex flex-col items-center gap-2" data-testid="end-call-button">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg transition-transform hover:scale-105 active:scale-95">
              <PhoneOff className="h-7 w-7" aria-hidden="true" />
            </span>
            <span className="text-xs text-white/70">End</span>
          </button>
        </div>
      )}
    </div>
  );
}
